import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '../../../i18n';
import './LanguageSwitcher.css';

const LANGUAGE_STORAGE_KEY = 'crypxa_lang';

const LanguageSwitcher: React.FC<{ variant?: 'public' | 'dashboard' }> = ({ variant = 'public' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  const handleSelect = (code: LanguageCode) => {
    i18n.changeLanguage(code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    setIsOpen(false);
  };

  return (
    <div className={`language-switcher language-switcher--${variant}`} ref={ref}>
      <button
        type="button"
        className="language-switcher-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Select language (${current.label})`}
      >
        <img src={current.flagUrl} alt="" className="language-switcher-flag" width={24} height={18} aria-hidden />
        <i className={`fas fa-chevron-down language-switcher-chevron ${isOpen ? 'open' : ''}`} aria-hidden />
      </button>
      {isOpen && (
        <ul
          className="language-switcher-dropdown"
          role="listbox"
          aria-label="Language options"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <li key={lang.code} role="option" aria-selected={i18n.language === lang.code}>
              <button
                type="button"
                className={`language-switcher-option ${i18n.language === lang.code ? 'active' : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                <img src={lang.flagUrl} alt="" className="language-switcher-option-flag" width={22} height={16} aria-hidden />
                <span>{lang.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
